import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Machine } from '@bridgewerk/shared';
import { useAuth } from '../features/auth/useAuth.js';
import { getApiClient } from '../lib/api-client.js';
import { machineKeys } from '../lib/query-keys.js';
import { useMode } from './ModeContext.js';

export interface MachineContextValue {
  readonly machines: Machine[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly refetch: () => void;
}

const MachineContext = createContext<MachineContextValue | null>(null);

export function MachineProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const { shopId } = useAuth();
  const { effectiveMachineIds } = useMode();

  const query = useQuery({
    queryKey: shopId ? machineKeys.list(shopId) : ['machines', 'none'],
    queryFn: () => getApiClient().getMachines(shopId ?? ''),
    enabled: !!shopId,
    staleTime: 30_000,
  });

  const machines = useMemo<Machine[]>(() => {
    const all = query.data ?? [];
    // API already filters for non-managers. Additional client-side filter
    // is applied only during manager impersonation.
    if (!effectiveMachineIds) return all;
    return all.filter(m => effectiveMachineIds.has(m.machine_id));
  }, [query.data, effectiveMachineIds]);

  const value = useMemo<MachineContextValue>(() => ({
    machines,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  }), [machines, query.isLoading, query.isError, query.refetch]);

  return <MachineContext.Provider value={value}>{children}</MachineContext.Provider>;
}

export function useMachines(): MachineContextValue {
  const ctx = useContext(MachineContext);
  if (!ctx) throw new Error('useMachines must be used inside <MachineProvider>');
  return ctx;
}
